import React from "react";
import clsx from "clsx";
import { ShoppingBag } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Card, Badge } from "../../components/ui/Card";
import { Button } from "../../components/ui/Button";

export function StatCard({ icon: Icon, label, value, testId, accent }) {
  return (
    <Card className="p-5 flex flex-col justify-between animate-fadeUp" data-testid={testId}>
      <div className="flex items-center gap-2 mb-3">
        <Icon size={16} className="text-primary" />
        <p className="text-xs font-bold uppercase tracking-[0.15em] text-slate-400">{label}</p>
      </div>
      <p className={`font-heading text-2xl font-black ${accent || "metallic-text"}`}>{value}</p>
    </Card>
  );
}

export function PageHeader({ title, subtitle }) {
  return (
    <div className="mb-6">
      <h1 className="font-heading text-2xl font-bold text-white">{title}</h1>
      {subtitle && <p className="text-sm text-slate-500 mt-1">{subtitle}</p>}
    </div>
  );
}

export function InactiveNotice({ label }) {
  const navigate = useNavigate();
  return (
    <Card className="p-8 text-center animate-fadeUp" data-testid="inactive-notice-card">
      <Badge status="pending" className="mb-4">Account Pending</Badge>
      <p className="text-sm text-slate-400 mb-5 max-w-md mx-auto">
        {label || "This section unlocks once your package is activated."} Complete your registration by selecting a
        package and activating via online payment or a Package PIN.
      </p>
      <Button onClick={() => navigate("/checkout")} data-testid="inactive-notice-checkout-button">
        <ShoppingBag size={16} /> Continue to Package Selection
      </Button>
    </Card>
  );
}

export function EmptyState({ children, testId }) {
  return <p className="text-sm text-slate-500 py-6 text-center" data-testid={testId}>{children}</p>;
}

/** Display-only cleanup for member-facing transaction descriptions - strips the internal
 * "(Weight N)" figure that Booster Pool descriptions carry (2026-08-26). The stored
 * description/weight value itself is untouched everywhere else (Admin views, DB, audit). */
export function cleanTransactionDescription(description) {
  if (!description) return description;
  return description.replace(/\s*\(Weight\s*\d+\)/i, "");
}

export function Tabs({ tabs, active, onChange, testId }) {
  return (
    <div className="flex gap-1.5 mb-5 flex-wrap" data-testid={testId}>
      {tabs.map((t) => (
        <button
          key={t.key}
          onClick={() => onChange(t.key)}
          data-testid={`${testId}-${t.key}`}
          className={clsx(
            "px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200",
            active === t.key
              ? "bg-primary/10 text-primary-light border border-primary/30"
              : "text-slate-400 hover:text-white hover:bg-white/5 border border-transparent"
          )}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

/** Server-side paginated + searchable history/list section - search input (debounced by the
 * caller via usePaginatedList), ResponsiveTable, loading/error/empty states, and
 * "Showing X-Y of Z" + Prev/Next pagination controls. Never fetches or filters more than one
 * page of data at a time (2026-09-17 Android WebView perf fix). */
export function PaginatedSection({
  testId, searchable = true, searchPlaceholder, searchValue, onSearchChange,
  columns, rows, keyField, page, limit, total, onPageChange,
  isLoading, isError, onRetry, emptyText, searchEmptyText,
}) {
  const totalPages = Math.max(1, Math.ceil(total / limit));
  const from = total === 0 ? 0 : (page - 1) * limit + 1;
  const to = Math.min(page * limit, total);
  return (
    <div data-testid={`${testId}-section`}>
      {searchable && (
        <input
          type="text"
          value={searchValue}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder={searchPlaceholder || "Search..."}
          data-testid={`${testId}-search-input`}
          className="w-full sm:w-72 mb-4 rounded-lg px-3 py-2 text-sm !text-white bg-white/[0.04] border border-white/15 placeholder:text-[#7c8aa8] outline-none focus:border-primary/50"
        />
      )}
      {isLoading ? (
        <div className="space-y-2" data-testid={`${testId}-loading`}>
          <div className="h-10 rounded bg-white/[0.05] animate-pulse" />
          <div className="h-10 rounded bg-white/[0.05] animate-pulse" />
          <div className="h-10 rounded bg-white/[0.05] animate-pulse" />
        </div>
      ) : isError ? (
        <div className="text-center py-8" data-testid={`${testId}-error`}>
          <p className="text-sm text-red-400 mb-3">Unable to load data. Please try again.</p>
          <Button size="sm" variant="secondary" onClick={onRetry} data-testid={`${testId}-retry-button`}>Retry</Button>
        </div>
      ) : (
        <>
          <ResponsiveTable
            columns={columns}
            rows={rows}
            keyField={keyField}
            testId={testId}
            emptyText={searchValue ? (searchEmptyText || "No matching records found.") : (emptyText || "No records found.")}
          />
          {total > 0 && (
            <div className="flex flex-wrap items-center justify-between gap-3 mt-4" data-testid={`${testId}-pagination`}>
              <p className="text-xs text-slate-500">Showing {from}-{to} of {total}</p>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => onPageChange(page - 1)}
                  disabled={page <= 1}
                  data-testid={`${testId}-prev-page`}
                  className="px-3 py-1.5 rounded-lg text-xs font-medium border border-white/10 text-slate-300 disabled:opacity-40 hover:bg-white/5 transition-colors"
                >
                  Prev
                </button>
                <span className="px-3 py-1.5 text-xs text-slate-400" data-testid={`${testId}-page-indicator`}>Page {page} of {totalPages}</span>
                <button
                  onClick={() => onPageChange(page + 1)}
                  disabled={page >= totalPages}
                  data-testid={`${testId}-next-page`}
                  className="px-3 py-1.5 rounded-lg text-xs font-medium border border-white/10 text-slate-300 disabled:opacity-40 hover:bg-white/5 transition-colors"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/** Renders a desktop table AND a mobile stacked-card list from the same column/row data -
 * per design mandate, complex tables must never be squeezed onto mobile. */
export function ResponsiveTable({ columns, rows, keyField, testId, emptyText }) {
  if (!rows || rows.length === 0) return <EmptyState testId={`${testId}-empty`}>{emptyText || "No records found."}</EmptyState>;
  return (
    <>
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full text-sm" data-testid={testId}>
          <thead>
            <tr className="border-b border-white/10 text-left text-slate-400 uppercase text-xs tracking-wider">
              {columns.map((c) => <th key={c.key} className="px-3 py-2">{c.label}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row[keyField]} className="border-b border-white/5" data-testid={`${testId}-row-${row[keyField]}`}>
                {columns.map((c) => <td key={c.key} className="px-3 py-2">{c.render ? c.render(row) : row[c.key]}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="md:hidden space-y-3" data-testid={`${testId}-mobile-list`}>
        {rows.map((row) => (
          <div key={row[keyField]} className="rounded-lg border border-white/10 bg-white/[0.02] p-3 space-y-1.5" data-testid={`${testId}-mobile-card-${row[keyField]}`}>
            {columns.map((c) => (
              <div key={c.key} className="flex justify-between items-start text-sm gap-3">
                <span className="text-slate-500 text-[11px] uppercase tracking-wide shrink-0 pt-0.5">{c.label}</span>
                <span className="text-right text-slate-200">{c.render ? c.render(row) : row[c.key]}</span>
              </div>
            ))}
          </div>
        ))}
      </div>
    </>
  );
}
